import { Modal } from 'antd';
import { Keyboard, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUiStore } from '@shared/stores/uiStore';

interface ShortcutRow {
  label: string;
  keys: string[];
}

interface ShortcutGroup {
  title: string;
  rows: ShortcutRow[];
}

function isMac(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad/i.test(navigator.platform);
}

function modKey(): string {
  return isMac() ? '⌘' : 'Ctrl';
}

function Kbd({ children }: { children: string }) {
  return <span className="ds-kbd ds-kbd--lg">{children}</span>;
}

export function ShortcutSheet() {
  const { t } = useTranslation('components');
  const open = useUiStore((s) => s.shortcutSheetOpen);
  const setOpen = useUiStore((s) => s.setShortcutSheetOpen);

  const GROUPS: ShortcutGroup[] = [
    {
      title: t('shortcutSheet.groups.general'),
      rows: [
        { label: t('shortcutSheet.rows.openPalette'), keys: [modKey(), 'K'] },
        { label: t('shortcutSheet.rows.showShortcuts'), keys: [modKey(), '/'] },
        { label: t('shortcutSheet.rows.toggleTheme'), keys: ['⇧', modKey(), 'L'] },
        { label: t('shortcutSheet.rows.closePalette'), keys: ['Esc'] },
      ],
    },
    {
      title: t('shortcutSheet.groups.navigation'),
      rows: [
        { label: t('shortcutSheet.rows.paletteUpDown'), keys: ['↑', '↓'] },
        { label: t('shortcutSheet.rows.runCommand'), keys: ['↵'] },
      ],
    },
    {
      title: t('shortcutSheet.groups.content'),
      rows: [
        { label: t('shortcutSheet.rows.newArticle'), keys: [modKey(), 'K', '→', t('shortcutSheet.newArticleKey')] },
      ],
    },
  ];
  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      footer={null}
      closeIcon={<X size={16} />}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
          <Keyboard size={16} /> {t('shortcutSheet.title')}
        </span>
      }
      width={560}
      destroyOnHidden
    >
      <div className="ds-shortcut-groups">
        {GROUPS.map((g) => (
          <section key={g.title} className="ds-shortcut-group">
            <h4 className="ds-shortcut-group__title">{g.title}</h4>
            <ul className="ds-shortcut-list">
              {g.rows.map((r) => (
                <li key={r.label} className="ds-shortcut-row">
                  <span className="ds-shortcut-row__label">{r.label}</span>
                  <span className="ds-shortcut-row__keys">
                    {r.keys.map((k, idx) => (
                      <Kbd key={`${k}-${idx}`}>{k}</Kbd>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </Modal>
  );
}
